'use client'

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import ConfigAPI, { Config } from '../../../api/ConfigAPI'
import { useImageUploader } from '../../../hooks/useImageUploader'
import { updateSettings } from '../../../store/reducers/AccountSlice'
import { toastService } from '../../../services/ToastService'
import { TabV2, TabsContent, TabsList, TabsTrigger } from '../../../components/TabV2/TabV2'
import { CardContent, CardFooter, CardHeader, CardTitle, CardV2 } from '../../../components/CardV2/CardV2'
import { LabelV2 } from '../../../components/LabelV2/LabelV2'
import { InputV2 } from '../../../components/InputV2/InputV2'
import Switch from '../../../components/Switch/Switch'
import CONFIG from '../../../config/config'
import FileUploadWrapper from '../../../components/FileUploadWrapper/FileUploadWrapper'
import ThemeColorConfiguration from '../../../components/ThemeColorConfiguration/ThemeColorConfiguration'
import SelectInput from '../../../components/SelectInput/SelectInput'
import { TimezoneSelector } from '../../../components/TimezoneSelector/TimezoneSelector'
import InputShortText from '../../../components/InputShortText/InputShortText'
import { isValidEmail, isValidInstagram, isValidPhone, isValidText, isValidUrl } from '../../../shared/validation'
import PhoneNumberInput from '../../../components/PhoneNumberInput/PhoneNumberInput'
import PrimaryButton from '../../../components/PrimaryButton/PrimaryButton'
import TinyText from '../../../components/TinyText/TinyText'
import { PulseLoader } from 'react-spinners'
import { weekDayToTranslationKey } from '../../../shared/weekDayToTranslationKey';
import InputNumber from '../../../components/InputNumber/InputNumber'
import { cn } from '../../../lib/utils'
import ImportCalendarButton from '../../../components/ImportCalendarButton/ImportCalendarButton'
import { getStatusesRaw, orderStatusToTranslationKeys } from '../../../shared/orderStatusToTranslationKey'
import WarningNotice from '../../../components/WarningNotice/WarningNotice'

type Day = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export default function Component() {
  const [config, setConfig] = useState<Config>()
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { uploadImage } = useImageUploader('', response => response.data.url, 'images');
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const configResponse = await ConfigAPI.get()
        setConfig(configResponse.data)
      } catch (error) {
        console.error('Failed to fetch data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, []);

  const isValidPhoneFn = useCallback((value: any) => !!value && isValidPhone(value), []);

  const handleLocalInputChange = (key: keyof Config, value: any) => {
    handleInputChange(key, value);
    setHasChanged(true);
  };

  const handleUpdate = () => {
    setHasChanged(false);
    updateConfig();
  }

  const handleInputChange = (key: keyof Config, value: any) => {
    setHasChanged(true);
    setConfig(prevConfig => ({ ...prevConfig, [key]: value }))
  }

  const updateConfig = async () => {
    setIsLoading(true)
    if (config) {
      await ConfigAPI.update(config)
      dispatch(updateSettings({
        tenantName: config.brandName,
        isConfigurationLoaded: true,
        logoUrl: config.logoUrl,
        allowedOrderStatuses: config.allowedOrderStatuses,
        openingHours: config.openingHours || '10:00',
        closingHours: config.closingHours || '17:00',
        timezone: config.timezone,
        mondayOpeningHours: config.mondayOpeningHours || '10:00',
        mondayClosingHours: config.mondayClosingHours || '17:00',
        mondayClosed: config.mondayClosed,
        tuesdayOpeningHours: config.tuesdayOpeningHours || '10:00',
        tuesdayClosingHours: config.tuesdayClosingHours || '17:00',
        tuesdayClosed: config.tuesdayClosed,
        wednesdayOpeningHours: config.wednesdayOpeningHours || '10:00',
        wednesdayClosingHours: config.wednesdayClosingHours || '17:00',
        wednesdayClosed: config.wednesdayClosed,
        thursdayOpeningHours: config.thursdayOpeningHours || '10:00',
        thursdayClosingHours: config.thursdayClosingHours || '17:00',
        thursdayClosed: config.thursdayClosed,
        fridayOpeningHours: config.fridayOpeningHours || '10:00',
        fridayClosingHours: config.fridayClosingHours || '17:00',
        fridayClosed: config.fridayClosed,
        saturdayOpeningHours: config.saturdayOpeningHours || '10:00',
        saturdayClosingHours: config.saturdayClosingHours || '17:00',
        saturdayClosed: config.saturdayClosed,
        sundayOpeningHours: config.sundayOpeningHours || '10:00',
        sundayClosingHours: config.sundayClosingHours || '17:00',
        sundayClosed: config.sundayClosed,
        fittingRooms: config.fittingRooms || 1
      }))
    }
    setIsLoading(false)
  }

  const updateCurrency = async (currency: string) =>  {
    setHasChanged(true);
    const updatedConfig = (await ConfigAPI.update({...config, currency: currency})).data;
    setConfig(prevConfig => updatedConfig);
  }

  const uploadLogo = async (file: File) => {
    try {
      const fileUrl = await uploadImage(file, `configuration/logo`)
      if (!fileUrl) {
        throw new Error('File upload failed')
      }
      handleInputChange('logoUrl', fileUrl)
    } catch (error) {
      toastService.showToast(t('toast.errors.logo'), 'error')
    }
  }

  const updateColorConfiguration = async (primaryColor: string, backgroundColor: string) => {
    const updatedConfig = {...config, primaryColor, backgroundColor };
    setConfig(prevConfig => updatedConfig);
    (await ConfigAPI.update(updatedConfig));
  };

  return (
    <div className="container mx-auto">
      <TabV2 defaultValue="general" className="w-full h-auto">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 py-[15px]">
          <TabsTrigger value="general">{t("settings.general.basic_info")}</TabsTrigger>
          <TabsTrigger value="appareance">{t("settings.general.appareance")}</TabsTrigger>
          <TabsTrigger value="delivery">{t("admin.form.delivery_data")}</TabsTrigger>
          <TabsTrigger value="billing">{t("admin.form.fiscal_data")}</TabsTrigger>
          <TabsTrigger value="orders">{t("settings.general.orders")}</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <div className="space-y-6">
            <CardV2>
              <CardHeader>
                <CardTitle>{t("settings.general.basic_info")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid">
                    <InputShortText
                      label={t("settings.general.basic_info.brand_name")!}
                      validationFn={(value) => !!value && isValidText(value, 2, 50)}
                      onChange={(e: any) => handleLocalInputChange('brandName', e.target.value)}
                      errorMessage={"errors.too_short"}
                      defaultValue={config?.brandName}
                      isDisabled
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <InputShortText
                    label={t("settings.general.basic_info.website")!}
                    validationFn={(value) => !!value && isValidUrl(value)}
                    onChange={(e: any) => handleLocalInputChange('websiteUrl', e.target.value)}
                    onValidationChange={(isValid) => null}
                    errorMessage={"errors.invalid_url"}
                    defaultValue={config?.websiteUrl}
                    isDisabled={isLoading}
                  />
                  <InputShortText
                    label={`${t("Instagram")}`}
                    validationFn={(value) => !!value && isValidInstagram(value)}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    errorMessage={"errors.invalid_instagram"}
                    defaultValue={config?.instagram || ''}
                    isDisabled={isLoading}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <TimezoneSelector
                      value={config?.timezone}
                      onChange={(value) => handleLocalInputChange('timezone', value)}
                      label={t("settings.general.basic_info.timezone")!}
                      isDisabled={false}
                      timezones={CONFIG.TIMEZONES}
                  />
                  <SelectInput
                      values={CONFIG.COUNTRY_CODES.sort((a, b) => a.countryName.localeCompare(b.countryName)).map(e => { return { value: e.countryCode, readableValue: e.countryName } })}
                      onChange={(e: any) => handleLocalInputChange('country', e.currentTarget.value)}
                      label={t("settings.general.basic_info.country")!}
                      defaultValue={config?.country || ""}
                      isDisabled={isLoading}
                  />
                  <InputNumber
                    label={t("settings.general.basic_info.fitting_rooms")!}
                    helperText="settings.general.basic_info.fitting_rooms_hint"
                    validationFn={(value) => !!value && value >= 1}
                    onChange={(e: any) => handleLocalInputChange('fittingRooms', parseInt(e.target.value, 10))}
                    errorMessage={"errors.minimum_zero"}
                    defaultValue={config?.fittingRooms || 1}
                    isDisabled={isLoading}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <div className="relative m-auto">
                    <PrimaryButton text={t("settings.general.basic_info.button")!} fn={() => handleUpdate()} isDisabled={isLoading || !hasChanged}/>
                </div>
              </CardFooter>
            </CardV2>

            <CardV2>
              <CardHeader>
                <CardTitle>{t("settings.general.basic_info.business_hours")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                    <div key={day} className="flex flex-wrap items-center space-x-2">
                      <LabelV2 htmlFor={`${day.toLowerCase()}Hours`} className="w-full sm:w-20 mb-2 sm:mb-0">{t(weekDayToTranslationKey(index))}</LabelV2>
                      <div className="flex items-center space-x-2 w-full sm:w-auto">
                        <InputV2
                          id={`${day.toLowerCase()}OpeningHours`}
                          type="time"
                          className="w-24"
                          value={config?.[`${day.toLowerCase()}OpeningHours` as `${Day}OpeningHours`] || '10:00'} 
                          onChange={(e) => handleInputChange(`${day.toLowerCase()}OpeningHours` as keyof Config, e.target.value)}
                          disabled={isLoading}
                        />
                        <span>-</span>
                        <InputV2
                          id={`${day.toLowerCase()}ClosingHours`}
                          type="time"
                          className="w-24"
                          value={config?.[`${day.toLowerCase()}ClosingHours` as `${Day}ClosingHours`] || '17:00'} 
                          onChange={(e) => handleInputChange(`${day.toLowerCase()}ClosingHours` as keyof Config, e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="flex items-center space-x-2 mt-2 sm:mt-0">
                        <Switch
                          id={`${day.toLowerCase()}Closed`}
                          checked={config ? config[`${day.toLowerCase()}Closed` as keyof Config] as boolean : false}
                          onChange={() => handleInputChange(`${day.toLowerCase()}Closed` as keyof Config, config && !config[`${day.toLowerCase()}Closed` as keyof Config] as boolean)}
                          className={cn(
                            "data-[state=checked]:bg-muted data-[state=unchecked]:bg-primary",
                            "data-[state=checked]:border-muted-foreground data-[state=unchecked]:border-primary"
                          )}
                        />
                        <LabelV2 htmlFor={`${day.toLowerCase()}Closed`}>
                          { config?.[`${day.toLowerCase()}Closed` as `${Day}Closed`] ? 
                            t("settings.general.basic_info.closed_state")
                            : 
                            t("settings.general.basic_info.open_state")
                          }
                        </LabelV2>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter>
                <div className="relative m-auto">
                    <PrimaryButton text={t("settings.general.basic_info.button")!} fn={() => handleUpdate()} isDisabled={isLoading || !hasChanged}/>
                </div>
              </CardFooter>
            </CardV2>

            <CardV2>
              <CardHeader>
                <CardTitle>{t("settings.general.basic_info.contact_information")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputShortText
                    label={`${t("providers.form.email")}`}
                    validationFn={(value) => !!value && isValidEmail(value)}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    errorMessage={"errors.invalid_email"}
                    defaultValue={config?.email || ''}
                    isDisabled={isLoading}
                  />
                  <PhoneNumberInput 
                    validationFn={isValidPhoneFn} 
                    label={`${t("contracts.contact_phone")}`} 
                    onChange={(e) => handleInputChange('phoneNumber', e)}
                    defaultValue={config?.phoneNumber}
                    isDisabled={isLoading}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <div className="relative m-auto">
                    <PrimaryButton text={t("settings.general.basic_info.button")!} fn={() => handleUpdate()} isDisabled={isLoading || !hasChanged}/>
                </div>
              </CardFooter>
            </CardV2>

            <CardV2>
              <CardHeader>
                <CardTitle>{t("settings.general.import.title")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p>{t("settings.general.import.info")}.</p>
                <WarningNotice message={t("settings.general.import.warning")} />
                <ImportCalendarButton />
              </CardContent>
            </CardV2>
          </div>
        </TabsContent>
        <TabsContent value="appareance">
          <CardV2>
            <CardHeader>
              <CardTitle>{t("settings.general.appareance")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <img src={config?.logoUrl} className="max-w-xs" alt="Uploaded Logo"/>
                  <FileUploadWrapper
                    onChange={(file) => {
                        if (file) {
                            uploadLogo(file);
                        }
                    }}
                    allowedTypes={CONFIG.IMAGE_TYPES}
                    isDisabled={isLoading}
                    maxSize={1.5}
                    allowedMultipleFiles={false}
                  />
                </div>
                <ThemeColorConfiguration onColorsChange={updateColorConfiguration} color={config?.primaryColor} defaultBgColor={config?.backgroundColor}/>
              </div>
            </CardContent>
            <CardFooter>
              <div className="relative m-auto">
                  { isLoading && <PulseLoader color="#fdd1d1" /> }
              </div>
            </CardFooter>
          </CardV2>
        </TabsContent>
        <TabsContent value="orders">
          <CardV2>
            <CardHeader>
              <CardTitle>{t("settings.general.workflows")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={config?.automaticallySendToPartners || false}
                  onChange={() => handleInputChange('automaticallySendToPartners', config && !config['automaticallySendToPartners'] as boolean)}
                  disabled={isLoading}
                />
                <LabelV2 htmlFor="automaticallySendToPartners">
                  {t("settings.general.workflows.send_automatically")}.
                </LabelV2>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={config?.automaticallySendToProduction || false}
                  onChange={() => handleInputChange('automaticallySendToProduction', config && !config['automaticallySendToProduction'] as boolean)}
                  disabled={isLoading}
                />
                <LabelV2 htmlFor="automaticallySendToProduction">
                  {t("settings.general.workflows.send_to_production_automatically")}.
                </LabelV2>
              </div>
            </CardContent>
            <CardFooter>
              <div className="relative m-auto">
                <PrimaryButton 
                  text={t("settings.general.basic_info.button")!} 
                  fn={handleUpdate} 
                  isDisabled={isLoading || !hasChanged}
                />
              </div>
            </CardFooter>
          </CardV2>
          <div className="h-2" />
          <CardV2>
            <CardHeader>
              <CardTitle>{t("settings.general.orders.available_statuses")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <TinyText text={t("settings.general.orders.available_statuses_hint") || "If you use the internal production queue, select which statuses are available to your team."} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getStatusesRaw().map((status) => {
                  const allEnabledByDefault = !config?.allowedOrderStatuses || config.allowedOrderStatuses.length === 0;
                  const isChecked = allEnabledByDefault ? true : (config?.allowedOrderStatuses || []).includes(status);
                  return (
                    <div key={status} className="flex items-center justify-between p-3 border rounded-md">
                      <div className="text-sm text-muted-foreground">{t(orderStatusToTranslationKeys(status))}</div>
                      <Switch
                        checked={isChecked}
                        onChange={() => {
                          const next = new Set((!config?.allowedOrderStatuses || config.allowedOrderStatuses.length === 0) ? getStatusesRaw() : (config?.allowedOrderStatuses || []));
                          if (isChecked) {
                            next.delete(status);
                          } else {
                            next.add(status);
                          }
                          handleInputChange('allowedOrderStatuses', Array.from(next));
                        }}
                        disabled={isLoading}
                      />
                    </div>
                  )
                })}
              </div>
            </CardContent>
            <CardFooter>
              <div className="relative m-auto">
                <PrimaryButton 
                  text={t("settings.general.basic_info.button")!} 
                  fn={handleUpdate}
                  isDisabled={isLoading || !hasChanged}
                />
              </div>
            </CardFooter>
          </CardV2>
        </TabsContent>
        <TabsContent value="billing">
          <CardV2>
            <CardHeader>
              <CardTitle>{t("admin.form.fiscal_data")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                  <TinyText text={`${t("settings.general.set_currency.description")}`} />
                  <SelectInput
                      values={CONFIG.SUPPORTED_CURRENCIES.map(e => { return { value: e, readableValue: e } })}
                      onChange={e => updateCurrency(e.target.value)}
                      label={t("settings.general.set_currency.label")!}
                      defaultValue={config?.currency || "EUR"}
                      isDisabled={isLoading}
                  />
              </div>
              <InputShortText 
                  label={"settings.general.specific_info.tax_id"} 
                  validationFn={(value) => !!value && isValidText(value, 3)} 
                  onChange={(e: any) => handleLocalInputChange('taxId', e.target.value)}
                  errorMessage={"errors.too_short"}
                  defaultValue={config?.taxId || ''}
                  placeholder={"admin.form.tax_id.placeholder"}
              />
              <InputShortText 
                  label={"settings.general.specific_info.tax_name"} 
                  validationFn={(value) => !!value && isValidText(value, 3)} 
                  onChange={(e: any) => handleLocalInputChange('companyName', e.target.value)}
                  errorMessage={"errors.too_short"}
                  defaultValue={config?.companyName || ''}
                  placeholder={"admin.form.tax_company_name.placeholder"}
              />
              <div className="flex flex-wrap space-x-5">
              <InputShortText 
                  label={"settings.general.specific_info.tax_address"} 
                  validationFn={(value) => !!value && isValidText(value, 5)} 
                  onChange={(e: any) => handleLocalInputChange('companyAddress', e.target.value)}
                  errorMessage={"errors.too_short"}
                  defaultValue={config?.companyAddress || ''}
                  placeholder={"admin.form.tax_company_address.placeholder"}
              />
              <InputShortText 
                  label={"settings.general.specific_info.tax_postalcode"} 
                  validationFn={(value) => !!value && isValidText(value, 2, 15)} 
                  onChange={(e: any) => handleLocalInputChange('companyPostalCode', e.target.value)}
                  errorMessage={"errors.too_short"}
                  defaultValue={config?.companyPostalCode}
                  placeholder={"admin.form.tax_postal_code.placeholder"}
              />
              </div>
              <InputShortText 
                  label={"settings.general.specific_info.bank_account"} 
                  validationFn={(value) => !!value && isValidText(value, 7, 35)} 
                  onChange={(e: any) => handleLocalInputChange('bankAccount', e.target.value)}
                  errorMessage={"errors.too_long_short"}
                  defaultValue={config?.bankAccount}
                  placeholder={"admin.form.bank_account.placeholder"}
              />
            </CardContent>
            <CardFooter>
              <div className="relative m-auto">
                  <PrimaryButton text={t("settings.general.basic_info.button")!} fn={() => handleUpdate()} isDisabled={isLoading || !hasChanged}/>
              </div>
            </CardFooter>
          </CardV2>
        </TabsContent>
        <TabsContent value="delivery">
          <CardV2 className="space-y-4">
            <CardHeader>
              <CardTitle>{t("admin.form.delivery_data")}</CardTitle>
            </CardHeader>
            <CardContent>
            <div className="space-y-4">
              <InputShortText 
                    label={"settings.general.specific_info.delivery_address"} 
                    validationFn={(value) => !!value && isValidText(value, 7)} 
                    onChange={(e: any) => handleLocalInputChange('deliveryAddress', e.target.value)}
                    errorMessage={"errors.too_short"}
                    defaultValue={config?.deliveryAddress || ''}
                    placeholder={"admin.form.address.placeholder"}
                />
                <InputShortText 
                    label={"settings.general.specific_info.delivery_postalcode"} 
                    validationFn={(value) => !!value && isValidText(value, 2, 15)} 
                    onChange={(e: any) => handleLocalInputChange('deliveryPostalCode', e.target.value)}
                    errorMessage={"errors.too_short"}
                    defaultValue={config?.deliveryPostalCode}
                    placeholder={"admin.form.postal_code.placeholder"}
                />
            </div>
            </CardContent>
            <CardFooter>
              <div className="relative m-auto">
                <PrimaryButton text={t("settings.general.basic_info.button")!} fn={() => handleUpdate()} isDisabled={isLoading || !hasChanged}/>
              </div>
            </CardFooter>
          </CardV2>
        </TabsContent>
      </TabV2>
    </div>
  )
}